import React, { useState } from 'react';
import styles from './HowTo.module.css';

// ── Content ──────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'quickstart',
    icon: '🚀',
    title: 'Quick Start',
    subtitle: 'Build your first recipe in under 5 minutes',
    steps: [
      { num: 1, heading: 'Name your recipe', body: 'Type a recipe name in the top bar — something like "Smoked Kielbasa v1". This is what gets saved to My Recipes.' },
      { num: 2, heading: 'Set your batch size', body: 'Enter the Meat+Fat Weight — the combined weight of your lean meat plus fat. The default is 1000g (about 2.2 lbs). Everything else is calculated as a percentage of this number, so scaling later is as simple as changing this one value.' },
      { num: 3, heading: 'Choose a style and method', body: 'Select a Sausage Style to get a recommended casing, and pick your Cooking Method. Hot Smoked and Cold Smoked have different cure requirements — the app will warn you.' },
      { num: 4, heading: 'Add ingredients', body: 'Click any ingredient section to expand it. Type or select an ingredient, then enter a percentage. Grams calculate instantly. You can also type grams and the percentage back-calculates. The Meat + Fat total should be 100% — the Formula Totals sidebar tracks this for you.' },
      { num: 5, heading: 'Save your work', body: "Click Save in the top-right corner. Your recipe is stored in this browser. To update it later, use Save (overwrites) or Save As… (creates a copy)." },
    ],
  },
  {
    id: 'calculator',
    icon: '🧮',
    title: 'The Calculator',
    subtitle: 'Understanding every field and section',
    subsections: [
      {
        heading: 'Top Bar Fields',
        items: [
          { label: 'Recipe Name', text: 'The name saved to My Recipes.' },
          { label: 'Sausage Style', text: 'Drives the recommended casing displayed below it.' },
          { label: 'Meat+Fat Weight Basis', text: 'The combined weight of your lean meat and fat. All ingredient percentages are calculated against this number. Use the dropdown to switch between grams and lbs.' },
          { label: 'Cooking Method', text: 'Hot Smoked, Cold Smoked, or Cooked. Cold Smoked displays a ⚠️ Cure #1 required warning.' },
          { label: 'Rating', text: 'Click stars to rate a recipe after making it. Saved with the recipe.' },
        ],
      },
      {
        heading: 'Ingredient Sections',
        text: 'There are 12 ingredient sections. Click any section header to expand it. Each row has a dropdown for the ingredient name, a % input, and a grams display. You can enter either % or grams — typing grams back-calculates the percentage.',
        items: [
          { label: 'MEAT', text: "Target 70–80% of meat weight. Many fatty cuts already contain 13–22% fat and may not need added fat." },
          { label: 'FAT', text: 'Target 20–30%. The Meat+Fat total in the sidebar should equal 100%.' },
          { label: 'LIQUIDS', text: "Ice or cold water is preferred — keeps the mix below 34°F. If using binders, use ≥ 10% liquid or the binder won't hydrate." },
          { label: 'SALT', text: 'Golden range: 1.75–2.0%. Never use iodized salt — it disrupts the nitrite curing reaction.' },
          { label: 'CURING AGENTS', text: 'Three options: Prague Powder #1 (synthetic, max 0.25% = 156 ppm), Cultured Celery Juice Powder (natural, 0.35–0.42%, pair with Acerola Cherry Powder, USDA label: "uncured"), or EcoCure #1 (polyphenol-based, 1% of meat weight, add last). All three show live PPM calculated with the correct formula for that specific curing agent.' },
          { label: 'BINDERS', text: 'Typical: 2–4%. Always pair with ≥ 10% liquid.' },
          { label: 'SPICES', text: 'Type any custom spice name directly. Target total: 0.5–2.0%.' },
          { label: 'CURING ACCELERATORS', text: 'Sodium erythorbate or Acerola Cherry Powder (0.05–0.10%). Pair with Cultured Celery Juice Powder as the natural cure. Must be added SEPARATELY from Cure #1 — if mixed directly they react prematurely.' },
        ],
      },
      {
        heading: 'Status Indicators',
        items: [
          { label: '✅ In Range', text: 'Within the typical recommended range.' },
          { label: '⚠️ High', text: 'Above typical max but below hard max. Use caution.' },
          { label: '❌ Over Max', text: 'Exceeds the hard maximum. Reduce the percentage.' },
          { label: '📊 Low', text: 'Below the typical minimum.' },
          { label: '→100% button', text: 'Appears on meat and fat rows. Fills that row so Meat+Fat totals exactly 100%.' },
        ],
      },
      {
        heading: 'Method Tab',
        text: 'The Method tab sits next to the Ingredients tab in the main content area. It contains your step-by-step production instructions organized into four default sections: Casing Preparation, Meat Preparation & Stuffing, Cooking, and Storage.',
        items: [
          { label: 'Add / remove steps', text: 'Each section has a "+ Add step" button at the bottom and a ✕ button on each step to remove it. Click the step text to edit it directly.' },
          { label: 'Reorder steps', text: 'Each step has ↑ ↓ buttons on the right to move it up or down within its section.' },
          { label: 'Reorder sections', text: 'Each section header has ↑ ↓ buttons to move the entire section up or down in the list.' },
          { label: 'Rename sections', text: 'Click the section title text to rename it in place.' },
          { label: 'Add / remove sections', text: 'Use "+ Add section" at the top right to add a custom section. Remove any section with its ✕ button.' },
          { label: 'Reset to defaults', text: '"Reset to defaults" restores all four sections and the original pre-populated steps — including the four smoker/grill cooking profiles.' },
        ],
      },
    ],
  },
  {
    id: 'numbers',
    icon: '📐',
    title: 'Understanding the Numbers',
    subtitle: "Baker's percentages, fat ratios, PPM, and nutrition",
    subsections: [
      {
        heading: "Baker's Percentages",
        text: "Every ingredient is a percentage of your total Meat+Fat Weight basis — meaning the combined weight of lean meat and fat you are starting with, not the total finished formula weight. If your recipe calls for 2% salt with 1000g of meat+fat, you need 20g of salt. This percentage never changes when you scale. To double a batch, change the Meat+Fat Weight from 1000g to 2000g. All gram amounts recalculate automatically.",
      },
      {
        heading: 'Fat Ratio — Two Numbers',
        items: [
          { label: 'Fat Ratio (total sausage)', text: 'Fat from ALL ingredients ÷ total formula weight. Useful for nutrition awareness.' },
          { label: 'Fat Ratio (meat+fat only)', text: 'Imagine you set all your other ingredients aside and just looked at your lean meat and fat. This ratio tells you what percentage of that meat+fat block is fat. For example, if you have 700g of pork shoulder and 300g of back fat, the fat ratio is 30%. This is the industry standard number — the "80/20" or "70/30" that recipes reference. Always compare against this number, not the total sausage ratio.' },
        ],
        callout: { type: 'tip', text: 'When a recipe says "use a 70/30 ratio," compare against Fat Ratio (meat+fat only), not the total sausage ratio.' },
      },
      {
        heading: 'Nitrite PPM',
        text: 'Prague Powder #1 is 6.25% sodium nitrite. The USDA limit for ground sausage is 156 ppm. At 0.25% of meat weight, Cure #1 delivers ~156 ppm — the absolute maximum. For natural curing: Cultured Celery Juice Powder at 0.42% delivers ~125 ppm. EcoCure #1 at 1% delivers ~100 ppm. The PPM calculator uses the correct conversion factor for each curing agent automatically.',
        callout: { type: 'warning', text: 'Nitrite is toxic at excessive levels regardless of source. Never exceed 0.25% Cure #1, 0.50% CCJP, or 1.0% EcoCure #1. Always verify independently.' },
      },
      {
        heading: 'Nutrition Estimate',
        text: 'All values are pre-cook estimates based on raw ingredient weights using USDA FoodData Central data. Cooking reduces moisture and concentrates nutrients. Use for general guidance, not precise labeling.',
      },
    ],
  },
  {
    id: 'saving',
    icon: '💾',
    title: 'Saving & Loading',
    subtitle: 'Managing your recipe collection',
    subsections: [
      {
        heading: 'Save Options',
        items: [
          { label: 'Save', text: "Overwrites the current recipe if already saved. Prompts for a name if it's new. Green 'saved' badge confirms it was written." },
          { label: 'Save As…', text: 'Always creates a new entry with a new name. Original is untouched. Use for variations.' },
          { label: 'Rename…', text: 'Changes the name without touching ingredients or data.' },
        ],
      },
      {
        heading: 'My Recipes',
        text: "Click My Recipes in the header to open your saved recipe list. Click Load to restore a recipe. The 🗑 icon deletes after confirmation. Recipes are stored in this browser's local storage — they stay between sessions but are specific to this browser and device.",
        callout: { type: 'tip', text: "Deleted recipes disappear from the list immediately — no need to close and reopen the panel." },
      },
    ],
  },
  {
    id: 'ingredients_db',
    icon: '🗂️',
    title: 'Ingredient Database',
    subtitle: 'View, edit, and add ingredients across all 12 sections',
    subsections: [
      {
        heading: 'Opening the Database',
        text: 'Click the "🗂️ Ingredients" button in the header to open the full ingredient database. It shows all built-in ingredients organized across 12 sections, with their typical percentage ranges, hard maximums, cost fields, and notes.',
      },
      {
        heading: 'Browsing and Searching',
        items: [
          { label: 'Left nav', text: 'Click any section (Lean Meat, Fat, Liquids, etc.) to filter the table to that section only. Click "All Ingredients" to see everything at once.' },
          { label: 'Search', text: 'The search bar at the top searches by name and note text across all sections simultaneously. Results update live as you type.' },
        ],
      },
      {
        heading: 'Editing Ingredients',
        text: 'Click the ✎ pencil icon on any row to edit it inline. You can change the name, typical % range, hard maximum, cost, cost unit, and notes. Click ✓ to save or ✕ to cancel. Click the ✕ delete button to remove an ingredient after confirmation.',
        callout: { type: 'warning', text: 'Deleting a built-in ingredient removes it from the database permanently (for this browser). Use "Reset to defaults" in the top-right corner to restore everything to the original built-in list.' },
      },
      {
        heading: 'Adding New Ingredients',
        items: [
          { label: 'From the database', text: 'Navigate to the section you want to add to, then click "+ Add ingredient" at the top right or at the bottom of the list. Fill in the name, % ranges, cost, and notes.' },
          { label: 'From the calculator', text: 'In any ingredient dropdown in the calculator, scroll to the bottom and select "+ Add new ingredient…". This opens the same Add modal, adds the ingredient to the database, and pre-fills it in the current row — no need to leave the calculator.' },
        ],
      },
      {
        heading: 'Cost Fields',
        text: 'Each ingredient has a Cost and Unit field. Cost is price per unit (e.g. $3.49 per lb). Unit options are: g, oz, lb, kg. Default units are pre-set by section: meat and fat default to per lb, liquids to per g, everything else to per oz. These costs are used by the Cost Estimator to calculate total batch cost and cost per sausage.',
        callout: { type: 'tip', text: 'You only need to set prices once — the database is global and shared across all recipes. Update a price here and every recipe that uses that ingredient reflects the new cost automatically.' },
      },
    ],
  },
  {
    id: 'scaling',
    icon: '⚖️',
    title: 'Scaling Recipes',
    subtitle: 'Resize any batch while keeping all ratios identical',
    steps: [
      { num: 1, heading: 'Open Scale Recipe', body: 'Click Scale Recipe in the header.' },
      { num: 2, heading: 'Choose a base recipe', body: 'Select any saved recipe as the starting point.' },
      { num: 3, heading: 'Choose a scaling mode', body: 'Meat Weight: enter a new meat weight in g or lbs.\n\nSausage Count: enter a target number of sausages. Uses the Avg Sausage Weight from the Casing Calculator (in the Recipe Stats sidebar) to determine total mix needed, then back-calculates the required meat weight. Set Avg Sausage Weight first for accurate results.' },
      { num: 4, heading: 'Review and apply', body: 'A preview table shows every ingredient with original and scaled gram amounts. Percentages are confirmed unchanged. Click Apply to load the scaled recipe.' },
    ],
    callout: { type: 'tip', text: 'Set Avg Sausage Weight in the Casing Calculator (Recipe Stats sidebar) based on real batch results — weigh a few finished sausages and average them. This makes sausage count scaling much more accurate.' },
  },
  {
    id: 'casing',
    icon: '🌭',
    title: 'Casing Calculator',
    subtitle: 'Estimating casing length and link count',
    subsections: [
      {
        heading: 'Key Inputs',
        items: [
          { label: 'Casing', text: 'Select from the dropdown. The fill rate (grams per meter) loads automatically.' },
          { label: 'Link Length', text: 'Your desired link size in inches. Default: 6 inches.' },
          { label: 'Avg Sausage Weight', text: 'Leave blank and the calculator derives weight from fill rate × link length. Enter your actual empirical weight from a real batch for a more accurate link count.' },
        ],
        callout: { type: 'tip', text: 'After your first real batch, weigh a few finished sausages and average them. Enter that as Avg Sausage Weight for significantly more accurate link count estimates.' },
      },
      {
        heading: 'Calibrating Equipment Loss',
        callout: { type: 'tip', text: 'After your first real batch, weigh all the meat left behind in the grinder and stuffer combined. Enter that total as Equipment Loss in the Formula Totals section. This gives you a much more accurate Stuffable Weight and casing calculation on every future batch.' },
      },
      {
        heading: 'Outputs',
        items: [
          { label: 'Casing Needed', text: 'Meters and feet to stuff the stuffable weight at the fill rate.' },
          { label: '+15% Buffer', text: 'Recommended order amount. Natural casings vary and you lose some to setup, knots, and blowouts.' },
          { label: 'Est. Sausages', text: 'Estimated link count. Updates live as you change inputs.' },
        ],
      },
      {
        heading: 'Stuffable Weight',
        text: 'All casing calculations use Stuffable Weight = Total Weight minus Equipment Loss. Set Equipment Loss in the Formula Totals section of the Recipe Stats sidebar (default: 150g).',
      },
    ],
  },
  {
    id: 'tools',
    icon: '🔧',
    title: 'Tools & Calculators',
    subtitle: 'Five standalone calculators, each with Add to Recipe integration',
    subsections: [
      {
        heading: 'Opening Tools',
        text: 'Click the "🔧 Tools" button in the header. The left nav shows all five calculators. Results from each tool can be injected directly into the open recipe with one click.',
      },
      {
        heading: 'SPE Calculator',
        items: [
          { label: 'What it does', text: 'Calculates the gram breakdown of a Soy Protein Emulsion batch — how much soy protein isolate, vegetable oil, and water to combine.' },
          { label: 'Input', text: 'Enter SPE as a % of meat weight (recommended: 5–20%) or a fixed gram amount. The ratio defaults to 1:4:5 but is editable.' },
          { label: 'Add to Recipe', text: 'Injects Soy Protein Emulsion into the Fat section at the correct percentage, with a note showing component grams.' },
          { label: 'How to make it', text: 'Step 1: Using a food processor, blender, or whisk, combine the calculated water with soy protein isolate and mix until a shiny paste forms. Step 2: Add the calculated chilled oil and cut at high speed until a stable emulsion is obtained. The result should look and feel like soft cream cheese. Step 3: Store the emulsion refrigerated for up to 5 days, or freeze for longer storage.' },
          { label: 'Important', text: 'Always use Soy Protein Isolate (SPI, ≥90% protein), not Soy Protein Concentrate (SPC). SPC will not form a proper gel at the 1:4:5 ratio.' },
        ],
      },
      {
        heading: 'Fat Reduction Planner',
        items: [
          { label: 'What it does', text: 'Reads your current recipe, calculates how much back fat to remove and how much SPE to add to hit a target fat %.' },
          { label: 'Input', text: 'Enter your target total fat % (e.g. 8%). The tool shows current fat, grams to remove, and the SPE needed to replace it gram-for-gram.' },
          { label: 'Apply to Recipe', text: 'Reduces the back fat percentage and adds Soy Protein Emulsion to the recipe in one click. Requires a fat ingredient in the recipe.' },
        ],
      },
      {
        heading: 'Sodium → Salt Converter',
        items: [
          { label: 'What it does', text: 'Calculates how much of your 2% salt budget is consumed by high-sodium ingredients like soy sauce, Worcestershire, fish sauce, or miso.' },
          { label: 'Formula', text: 'Sodium (mg) × 2.54 = Salt (mg). Divide by 1000 to get grams. The 2.54 factor is the exact NaCl/Na molecular weight ratio (58.44/22.99).' },
          { label: 'Input', text: 'Select an ingredient from the presets (Soy Sauce, Fish Sauce, Worcestershire, etc.) or enter a custom sodium per 100g value. Enter the % you use in the recipe.' },
          { label: 'Output', text: 'Shows sodium contributed, salt equivalent, and a combined total including your Salt section. Color-coded: green (safe), amber (approaching 2% limit), red (over).' },
        ],
        callout: { type: 'tip', text: 'At 3% soy sauce alongside 1.75% table salt, your combined salt is already 2.19% — over the limit. Always check sodium-rich ingredients with this tool.' },
      },
      {
        heading: 'Brine Calculator',
        items: [
          { label: 'What it does', text: 'Calculates salt needed for a wet brine solution, or injection brine parameters for pump/injection curing.' },
          { label: 'Wet soak mode', text: 'Enter water amount and target brine %. Calculates exact grams of salt to dissolve. A reverse-lookup shows what brine % to use for a target salt level in the finished meat.' },
          { label: 'Injection mode', text: 'Enter brine % and pump % (brine injected as % of meat weight). Calculates resulting salt in meat and exact brine amounts needed.' },
          { label: 'Equilibrium note', text: 'For wet soaks, meat absorbs roughly 40–55% of the brine salt concentration over 24–48 hours. A 10% brine delivers approximately 4.7% salt in the meat at equilibrium.' },
        ],
      },
      {
        heading: 'Smoke Time Estimator',
        items: [
          { label: 'What it does', text: 'Estimates cook time in a hot smoker based on casing diameter and smoker temperature. Also provides the full incremental temperature schedule for cold smoking.' },
          { label: 'Casing diameter', text: 'Uses the casing selected in your recipe automatically, or enter a diameter manually.' },
          { label: 'Hot smoked', text: 'Enter smoker temp and target internal temp. Returns estimated cook time range. Always verify with a calibrated thermometer.' },
          { label: 'Cold smoked', text: 'Shows a four-stage incremental schedule (100°F bloom → 125°F → 150°F → 170°F finish). Includes a hard safety warning that Cure #1 or Cultured Celery Juice Powder is required.' },
        ],
        callout: { type: 'warning', text: 'Smoke time is an estimate only. The only safe measure is internal temperature. USDA minimum: 145°F with a 4-minute hold, or 160°F instant.' },
      },
    ],
  },
  {
    id: 'import',
    icon: '📥',
    title: 'Recipe Import',
    subtitle: 'Import gram-based recipes from twoguysandacooler.com or any similar site',
    subsections: [
      {
        heading: 'How to Get the Recipe Text',
        text: 'The import works by parsing plain text from any recipe page that lists ingredients in grams. The key is finding a clean, print-friendly version of the page that strips away ads, sidebars, and navigation.',
        items: [
          { label: 'Step 1 — Find a print page', text: 'Most recipe sites have a print-friendly URL. For sites using the WPRM plugin (common on food blogs), change the URL from /recipe-name/ to /wprm_print/recipe-name. Other sites have a print button that opens a clean version — use that page.' },
          { label: 'Step 2 — Select all and copy', text: 'Press Ctrl+A to select everything on the page, then Ctrl+C to copy it.' },
          { label: 'Step 3 — Paste and parse', text: 'Click 📥 Import in the header, paste into the textarea, and click Parse Recipe.' },
          { label: 'Format requirement', text: 'The parser looks for ingredient lines in the format "NUMBER UNIT ingredient name" — for example "700 g pork shoulder" or "15 g kosher salt". Any recipe site that uses metric gram measurements will work.' },
        ],
        callout: { type: 'tip', text: 'If parsing fails, try finding the print page for the recipe — print pages strip out the surrounding page chrome and give the parser much cleaner text to work with.' },
      },
      {
        heading: 'What Gets Imported',
        items: [
          { label: 'Recipe name', text: 'Extracted from the main heading and loaded into the Recipe Name field.' },
          { label: 'Description', text: 'The short tagline below the recipe name (e.g. "A New Orleans Staple") is placed into Recipe Notes automatically.' },
          { label: 'Ingredients', text: 'All ingredients listed with gram amounts are parsed, matched to our database, and loaded with the correct percentages. The meat+fat total is used as the basis for all percentages.' },
          { label: 'Instructions', text: 'All cooking sections and steps are imported and made available in the Method tab.' },
          { label: 'Items without amounts', text: 'Casings and optional items (listed without gram amounts) are flagged in Step 2 for reference. Add them manually in the Casing Calculator after importing.' },
        ],
        callout: { type: 'note', text: 'Prep time, cook time, star ratings, and serving size controls are stripped automatically — they are not relevant to the calculator.' },
      },
      {
        heading: 'Step 3 — Reviewing Ingredient Matches',
        text: 'This is the most important step. Each parsed ingredient is matched against our database and assigned a status:',
        items: [
          { label: '✅ Auto', text: 'Exact match via the built-in alias table. Common names like "pork shoulder", "insta cure #1", "kosher salt", and "black pepper" are pre-mapped to our database entries. No action needed.' },
          { label: '🔄 Review', text: 'A fuzzy keyword match was found and pre-selected, but you should confirm it. For example, "minced garlic" may match to "Garlic Powder" — correct if needed using the Change button.' },
          { label: '❓ No match', text: 'The ingredient name was not recognized. You have three options: click Search DB to find it by typing, click + Add New to create a new database entry on the spot (you name it and choose its section), or click Skip to exclude it from the import.' },
          { label: '⏭ Skipped', text: 'The ingredient will not be imported. Click Restore to bring it back.' },
        ],
        callout: { type: 'tip', text: 'The alias table grows over time. If you find a common name that should be auto-matched (like "savorye" → Summer Savory), note it and we can add it to the alias list.' },
      },
      {
        heading: 'After Import — What to Do Next',
        text: 'The calculator loads with all imported ingredients and percentages already filled in. The description is in Recipe Notes. Four things are intentionally left for you to set:',
        items: [
          { label: 'Sausage Style', text: 'Select from the top bar dropdown. This drives the recommended casing.' },
          { label: 'Cooking Method', text: 'Select Hot Smoked, Cold Smoked, or Cooked. This is important for cure requirements.' },
          { label: 'Casing', text: 'Open the Casing Calculator section in the left sidebar and select the casing type.' },
          { label: 'Rating', text: 'Leave blank until after you have made the recipe.' },
        ],
        callout: { type: 'tip', text: 'Save the recipe immediately after import so you do not lose the imported data. Use Save As… to give it a name like "Andouille — TwoGuys import".' },
      },
      {
        heading: 'Works With Other Sites Too',
        text: 'The importer is not specific to twoguysandacooler.com. It works with any recipe text that uses gram-based ingredient amounts in the format "number g ingredient name". If a site has a print page that produces clean text, you can import from it the same way.',
      },
    ],
  },
  {
    id: 'exporting',
    icon: '📄',
    title: 'Exporting to PDF',
    subtitle: 'Print-ready recipe sheets',
    steps: [
      { num: 1, heading: 'Open Export PDF', body: 'Click Export PDF in the header.' },
      { num: 2, heading: 'Choose what to include', body: 'Ingredients are always included. Toggle: Methods & Instructions, Nutrition Estimate, and Casing Calculator (greyed out if no casing selected).' },
      { num: 3, heading: 'Download', body: 'Click Download PDF. The file saves to your Downloads folder with the recipe name as the filename.' },
    ],
    callout: { type: 'tip', text: 'The PDF puts Casing Calculator and Nutrition Estimate side by side to save space. Methods & Instructions appear before these sections.' },
  },
  {
    id: 'cost',
    icon: '💰',
    title: 'Cost Estimator',
    subtitle: 'Real-time batch cost and per-sausage cost from ingredient prices',
    subsections: [
      {
        heading: 'Where It Lives',
        text: 'The Cost Estimator is a collapsible section in the left-side Recipe Stats sidebar, below the Casing Calculator. It updates in real time as you add or change ingredients — no button to press.',
      },
      {
        heading: 'Setting Prices',
        text: 'Prices are set globally in the Ingredient Database (🗂️ Ingredients button in the header). Find the ingredient, click the ✎ pencil to edit it, and fill in the Cost and Unit fields. You only need to set a price once — it applies to every recipe that uses that ingredient automatically.',
        items: [
          { label: 'Cost field', text: 'Enter the price you paid (e.g. 3.49).' },
          { label: 'Unit field', text: 'Select the unit that matches how you bought it: per lb, per oz, per g, or per kg. The calculator converts to cost-per-gram internally to handle any unit mix.' },
          { label: 'Casing cost', text: 'Set casing cost in the Casings section of the Ingredient Database. Use $ per yard or $ per foot. The Cost Estimator uses the +15% buffer footage from the Casing Calculator for a realistic cost.' },
        ],
        callout: { type: 'tip', text: 'You don\'t need to price every ingredient to get useful numbers. Even pricing just the meat and fat — the two most expensive items — gives you a meaningful floor estimate.' },
      },
      {
        heading: 'What It Shows',
        items: [
          { label: 'Per-ingredient rows', text: 'Each priced ingredient shows its name, the amount used (in its native unit), and the cost for that amount in this batch.' },
          { label: 'Casing cost row', text: 'If the casing has a price set, it appears as a separate line using the +15% buffer footage.' },
          { label: 'Total batch cost', text: 'Sum of all priced ingredients plus casing. Ingredients without prices are excluded and counted in the "X ingredients without price" note at the bottom.' },
          { label: 'Cost per sausage', text: 'Total batch cost divided by estimated sausage count. The count comes from dividing meat weight by Avg Sausage Weight (set in the Casing Calculator). Set Avg Sausage Weight from a real batch for an accurate per-sausage number.' },
        ],
      },
      {
        heading: 'Partial Pricing',
        text: 'You don\'t have to price everything. A note at the bottom of the section counts how many ingredients are missing prices and prompts you to set them in 🗂️ Ingredients. The total and per-sausage cost shown are always based only on what has been priced — treat them as a floor, not a ceiling, if some ingredients are unpriced.',
        callout: { type: 'tip', text: 'Update prices in 🗂️ Ingredients whenever ingredient costs change. All open recipes immediately reflect the new price — there\'s no per-recipe price to update.' },
      },
    ],
  },
  {
    id: 'safety',
    icon: '⚠️',
    title: 'Food Safety',
    subtitle: 'Critical rules for safe sausage making',
    subsections: [
      {
        heading: 'When is Cure #1 Required?',
        items: [
          { label: 'Cold Smoked — ALWAYS', text: 'Low temperatures (below 140°F) keep sausage in the bacterial danger zone for hours. Without nitrite, C. botulinum can produce toxin. No exceptions.' },
          { label: 'Hot Smoked — RECOMMENDED', text: 'Required when the sausage rests overnight before smoking. Without cure, bacteria can multiply during the rest period.' },
          { label: 'Overnight rest — REQUIRED', text: 'Any sausage stuffed and held overnight before cooking needs Cure #1 regardless of method.' },
          { label: 'Same-day cook — Optional', text: 'If you grind, stuff, and cook all on the same day without an overnight rest, cure is not strictly required.' },
        ],
        callout: { type: 'warning', text: 'Cold smoking without Cure #1 is dangerous. Never skip the cure step for cold-smoked products.' },
      },
      {
        heading: 'Cure #1 Rules',
        items: [
          { label: 'Maximum dose', text: '0.25% of meat weight = 156 ppm. The calculator enforces this limit.' },
          { label: 'Natural cure option', text: 'Cultured Celery Juice Powder (CCJP) at 0.42% delivers ~125 ppm nitrite naturally. Pair with Acerola Cherry Powder (0.05–0.10%) as a curing accelerator. USDA requires these products to be labeled "uncured / no nitrites added" even though they contain naturally derived nitrite. Chemically, the nitrite is identical to synthetic sources.' },
          { label: 'Never iodized salt', text: 'Iodine interferes with nitrite chemistry. Use non-iodized table salt, kosher salt, or fine sea salt only.' },
          { label: 'Curing accelerators', text: 'Sodium erythorbate and ascorbate must be added SEPARATELY from Cure #1 — if mixed directly they react prematurely.' },
          { label: 'Temperature control', text: 'Keep meat below 34°F throughout grinding and mixing. Warm meat causes fat smearing and accelerates bacterial growth.' },
        ],
      },
    ],
  },
  {
    id: 'share-help',
    icon: '🌶️',
    title: 'Share on Mesa Robles',
    subtitle: 'Publish a recipe, or open one someone shared',
    callout: { type: 'tip', text: 'Put a recipe you made in LinksLab on mesarobles.com, either for everyone or only for people you send the link to. Anyone can download it back into LinksLab and scale it to their own batch.' },
    subsections: [
      {
        heading: 'What gets shared',
        items: [
          { label: 'Included', text: 'The ingredients with their amounts in grams (and ounces), the method steps, the cure and nitrite figures with the safety note, and the numbers at a glance — batch size, stuffable and cooked weight, casing and links, nutrition per 100 g.' },
          { label: 'Never shared', text: 'Your other recipes, your settings, your backups — and never your ingredient prices or pricing settings. Whoever opens the recipe sees costs from their own price list, not yours.' },
        ],
      },
      {
        heading: '1. Export from LinksLab',
        items: [
          { label: 'Check it first', text: 'Open the recipe you want to share and check it one last time. The nitrite badge and warnings you see here are the safety check. Mesa Robles shows them exactly as they are.' },
          { label: 'Press Share', text: 'Press the Share button in the top bar, next to Export PDF.' },
          { label: 'Look over the preview', text: 'It shows exactly what will be published. Amber messages are warnings worth reading; they never stop you.' },
          { label: 'Press Download', text: 'A file named like andouille-sausage.mesarobles.json lands in your Downloads folder.' },
        ],
      },
      {
        heading: '2. Import on Mesa Robles',
        items: [
          { label: 'Go to the import page', text: 'Open mesarobles.com/admin/import.html and log in with your usual Mesa Robles admin login, then choose the file.' },
          { label: 'Red and amber', text: 'Red messages stop the import — for example, the file is a backup and not a shared recipe. Amber messages are warnings to read. Nitrite warnings appear here too.' },
          { label: 'Edit anything you like', text: 'Title, intro, origin and credit, ingredient wording, steps, notes. If you change an amount, the page warns you that the recipe page will no longer match the calculator file. The calculator file always keeps the original numbers.' },
          { label: 'Choose where it goes', text: 'For example Smoke & Salt then Curing & Sausage, which is what LinksLab suggests. Then choose Public or Unlisted, add photos if you like, and press Publish.' },
        ],
      },
      {
        heading: '3. Wait about two minutes',
        items: [
          { label: 'One site update', text: 'Publishing rebuilds the site once. That uses one site update (15 of the 300 monthly Netlify credits shared by Mesa Robles, LinksLab and PastramiLab; when they run out, updates wait until the monthly reset).' },
          { label: 'Save your updates', text: 'Finish all your edits in the import screen before you press Publish. Every later save in the admin uses another update.' },
          { label: 'Check the page', text: 'Open the link the import screen gives you and read it once as a visitor would. The Download to calculator link sits under the ingredients.' },
        ],
      },
      {
        heading: 'Public or Unlisted?',
        items: [
          { label: 'Public', text: 'Listed on Mesa Robles, in search and in the site map.' },
          { label: 'Unlisted', text: 'Not listed anywhere, but anyone with the link can open it, including anyone they forward it to. It is a private address, not a password.' },
        ],
      },
      {
        heading: 'Changing a published recipe',
        items: [
          { label: 'Words, photos, intro', text: 'Edit them in the Mesa Robles admin.' },
          { label: 'Amounts', text: 'Change the recipe in LinksLab, export again and import onto the same page. The import screen offers Update existing page. That keeps your photos, intro, origin, placement and hand-written notes, and replaces the amounts, steps and safety box.' },
        ],
      },
      {
        heading: 'Opening a recipe from Mesa Robles',
        items: [
          { label: 'Download it', text: 'On the recipe page, press Download to calculator.' },
          { label: 'Open it here', text: 'Open My Recipes and press Open shared recipe, then choose the file.' },
          { label: 'Check, then confirm', text: 'It is added as a new recipe. Nothing you already have is changed. If the name is already taken, the word shared is added to it.' },
          { label: 'From the other calculator', text: 'A recipe from PastramiLab is not opened here. The message tells you which app to use.' },
        ],
      },
      {
        heading: 'If something goes wrong',
        items: [
          { label: 'This is a shared recipe', text: 'You chose a shared recipe in the Backup screen. Use Open shared recipe in My Recipes instead — restoring would replace everything you have with one recipe.' },
          { label: 'This is a backup, not a recipe package', text: 'You chose a backup file in the share screen. Export with the Share button instead.' },
          { label: 'Newer format version', text: 'The file came from a newer calculator. Mesa Robles needs updating first.' },
          { label: 'The page did not appear', text: 'Check that the department and section match, and wait a few minutes for the rebuild. If you have used up this month of site updates, the change goes live when they reset.' },
        ],
      },
    ],
  },
];

// ── Component ────────────────────────────────────────────────
export default function HowTo({ onClose, initialSection }) {
  // Mesa Robles links straight to /#share-help, so a named section has to be
  // able to open directly rather than always landing on Quick Start.
  const [activeId, setActiveId] = useState(
    initialSection && SECTIONS.some(s => s.id === initialSection)
      ? initialSection
      : 'quickstart'
  );
  const activeSection = SECTIONS.find(s => s.id === activeId) || SECTIONS[0];

  return (
    <div className={styles.overlay}>

      {/* ── PAGE HEADER ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <span className={styles.pageIcon}>📖</span>
          <div>
            <div className={styles.pageTitle}>How-To Guide</div>
            <div className={styles.pageSubtitle}>Everything you need to know</div>
          </div>
        </div>
        <button className={styles.closeBtn} onClick={onClose}>✕ Close</button>
      </div>

      {/* ── BODY ── */}
      <div className={styles.body}>

        {/* ── LEFT NAV ── */}
        <nav className={styles.nav}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              className={`${styles.navItem} ${activeId === s.id ? styles.navActive : ''}`}
              onClick={() => setActiveId(s.id)}
            >
              <span className={styles.navIcon}>{s.icon}</span>
              <span className={styles.navLabel}>{s.title}</span>
            </button>
          ))}
        </nav>

        {/* ── CONTENT — only the active section ── */}
        <div className={styles.content}>
          <div className={styles.section}>

            {/* Section heading */}
            <div className={styles.sectionHead}>
              <span className={styles.sectionIcon}>{activeSection.icon}</span>
              <div>
                <h2 className={styles.sectionTitle}>{activeSection.title}</h2>
                <p  className={styles.sectionSubtitle}>{activeSection.subtitle}</p>
              </div>
            </div>

            {/* Top-level callout */}
            {activeSection.callout && <Callout {...activeSection.callout} />}

            {/* Placeholder (Cost Estimator etc) */}
            {activeSection.placeholder && (
              <Placeholder {...activeSection.placeholder} />
            )}

            {/* Steps */}
            {activeSection.steps && (
              <div className={styles.steps}>
                {activeSection.steps.map(st => (
                  <div key={st.num} className={styles.step}>
                    <div className={styles.stepNum}>{st.num}</div>
                    <div className={styles.stepBody}>
                      <div className={styles.stepHeading}>{st.heading}</div>
                      <p className={styles.stepText}>{st.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}



            {/* Subsections */}
            {activeSection.subsections && activeSection.subsections.map((sub, si) => (
              <div key={si} className={styles.subsection}>
                <h3 className={styles.subHeading}>{sub.heading}</h3>
                {sub.text && <p className={styles.subText}>{sub.text}</p>}
                {sub.items && (
                  <div className={styles.items}>
                    {sub.items.map((item, ii) => (
                      <div key={ii} className={styles.item}>
                        <div className={styles.itemLabel}>{item.label}</div>
                        <div className={styles.itemText}>{item.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                {sub.callout && <Callout {...sub.callout} />}
              </div>
            ))}

          </div>
        </div>
      </div>
    </div>
  );
}

function Callout({ type, text }) {
  const config = {
    tip:     { icon: '💡', label: 'Tip',     cls: 'calloutTip'     },
    warning: { icon: '⚠️', label: 'Warning', cls: 'calloutWarning' },
    note:    { icon: 'ℹ️', label: 'Note',    cls: 'calloutNote'    },
  };
  const c = config[type] || config.note;
  return (
    <div className={`${styles.callout} ${styles[c.cls]}`}>
      <span className={styles.calloutIcon}>{c.icon}</span>
      <div>
        <span className={styles.calloutLabel}>{c.label}: </span>
        <span className={styles.calloutText}>{text}</span>
      </div>
    </div>
  );
}

function Placeholder({ icon, title, text, items }) {
  return (
    <div className={styles.placeholder}>
      <div className={styles.placeholderIcon}>{icon}</div>
      <div className={styles.placeholderTitle}>{title}</div>
      <p className={styles.placeholderText}>{text}</p>
      {items && (
        <ul className={styles.placeholderList}>
          {items.map((item, i) => (
            <li key={i} className={styles.placeholderItem}>
              <span className={styles.placeholderBullet}>→</span>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
